import {
  useCallback, useMemo, useRef, useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';

import LeadReengagementSequence from '../../../../../Model/LeadReengagementSequence';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import { getContractIdFromOnboardingLink } from '../../../../utils/onboardingLink';
import { DEFAULT_COUNTRIES, DEFAULT_SALES_STAGES } from '../../utils/constants';
import { isBodySendable, substitutedLength } from '../../utils/composeBodyPreview';
import { toggleItemInList } from '../../utils/listSelection';
import { reportError } from '../../utils/reportError';
import { fromStoredImage } from '../../utils/uploadCampaignImage';

const DEFAULT_OFFSETS = [1, 14, 30];

const makeBlast = (overrides = {}) => ({
  key: uuidv4(),
  blastId: uuidv4(),
  dayOffset: 1,
  body: '',
  dpl: null,
  image: null,
  ...overrides,
});

const defaultBlasts = () => DEFAULT_OFFSETS.map((dayOffset) => makeBlast({ dayOffset }));

const defaultEligibility = () => ({
  salesStages: DEFAULT_SALES_STAGES,
  countries: DEFAULT_COUNTRIES,
  requireConsent: true,
});

// Only the url is stored, so the picker re-resolves the labelled option once contracts load.
const hydrateDpl = (url) => (url ? { contractId: getContractIdFromOnboardingLink(url), url } : null);

const hydrateBlast = (blast, sequenceDpl) => makeBlast({
  blastId: blast.blastId || uuidv4(),
  dayOffset: typeof blast.dayOffset === 'number' ? blast.dayOffset : 1,
  body: blast.bodyTemplate || '',
  dpl: hydrateDpl(blast.dpl || sequenceDpl),
  image: fromStoredImage(blast.image),
});

export const useReEngagementSequence = () => {
  const [coach, setCoach] = useState(null);
  const [sequenceId, setSequenceId] = useState(null);
  const [active, setActive] = useState(true);
  const [eligibility, setEligibility] = useState(defaultEligibility);
  const [blasts, setBlasts] = useState(defaultBlasts);
  const [loading, setLoading] = useState(false);

  const isMountedRef = useComponentMounted();
  const loadTokenRef = useRef(0);

  const resetSequence = useCallback(() => {
    setSequenceId(null);
    setActive(true);
    setEligibility(defaultEligibility());
    setBlasts(defaultBlasts());
  }, []);

  const handleSetCoach = useCallback(async (next) => {
    setCoach(next);
    const token = loadTokenRef.current + 1;
    loadTokenRef.current = token;

    if (!next || !next.id) {
      resetSequence();
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const existing = await LeadReengagementSequence.getByCoach(next.id);
      if (!isMountedRef.current || loadTokenRef.current !== token) return;
      if (existing) {
        setSequenceId(existing.id);
        setActive(existing.active !== false);
        const stored = existing.eligibility || {};
        setEligibility({
          salesStages: stored.salesStages?.length ? stored.salesStages : DEFAULT_SALES_STAGES,
          countries: stored.countries || DEFAULT_COUNTRIES,
          requireConsent: stored.requireConsent !== false,
        });
        setBlasts(
          Array.isArray(existing.blasts) && existing.blasts.length > 0
            ? existing.blasts.map((blast) => hydrateBlast(blast, existing.dpl))
            : defaultBlasts(),
        );
      } else {
        resetSequence();
      }
    } catch (err) {
      reportError('loadReengagementSequence', err);
      if (!isMountedRef.current || loadTokenRef.current !== token) return;
      resetSequence();
    } finally {
      if (isMountedRef.current && loadTokenRef.current === token) setLoading(false);
    }
  }, [resetSequence, isMountedRef]);

  const toggleSalesStage = useCallback((stage) => {
    setEligibility((prev) => ({ ...prev, salesStages: toggleItemInList(prev.salesStages, stage) }));
  }, []);

  const toggleCountry = useCallback((code) => {
    setEligibility((prev) => ({ ...prev, countries: toggleItemInList(prev.countries, code) }));
  }, []);

  const setRequireConsent = useCallback((value) => {
    setEligibility((prev) => ({ ...prev, requireConsent: value }));
  }, []);

  const addBlast = useCallback(() => {
    setBlasts((prev) => {
      const lastOffset = prev.length ? prev[prev.length - 1].dayOffset : 0;
      return [...prev, makeBlast({ dayOffset: lastOffset + 1 })];
    });
  }, []);

  const removeBlast = useCallback((key) => {
    setBlasts((prev) => prev.filter((b) => b.key !== key));
  }, []);

  const patchBlast = useCallback((key, patch) => {
    setBlasts((prev) => prev.map((b) => (b.key === key ? { ...b, ...patch } : b)));
  }, []);

  const updateBlastBody = useCallback((key, body) => patchBlast(key, { body }), [patchBlast]);
  const updateBlastOffset = useCallback(
    (key, dayOffset) => patchBlast(key, { dayOffset: Math.max(1, dayOffset) }),
    [patchBlast],
  );
  const updateBlastImage = useCallback((key, image) => patchBlast(key, { image }), [patchBlast]);
  const updateBlastDpl = useCallback((key, dpl) => patchBlast(key, { dpl }), [patchBlast]);

  const derived = useMemo(() => {
    const offsetCounts = blasts.reduce((acc, b) => {
      acc[b.dayOffset] = (acc[b.dayOffset] || 0) + 1;
      return acc;
    }, {});
    const coachName = coach ? coach.name : '';
    const blastsMeta = blasts.map((b) => {
      const subLen = substitutedLength(b.body, { coachName });
      const duplicateOffset = offsetCounts[b.dayOffset] > 1;
      return {
        key: b.key,
        duplicateOffset,
        valid: isBodySendable(b.body, subLen)
          && !!b.dpl?.contractId
          && b.dayOffset >= 1
          && !duplicateOffset,
      };
    });
    const blastsComplete = blasts.length > 0 && blastsMeta.every((m) => m.valid);
    const isValid = !!(coach && coach.id)
      && eligibility.salesStages.length > 0
      && eligibility.countries.length > 0
      && blastsComplete
      && !loading;
    return { blastsMeta, coachName, isValid };
  }, [coach, eligibility, blasts, loading]);

  return {
    coach,
    setCoach: handleSetCoach,
    sequenceId,
    setSequenceId,
    active,
    setActive,
    eligibility,
    toggleSalesStage,
    toggleCountry,
    setRequireConsent,
    blasts,
    addBlast,
    removeBlast,
    updateBlastBody,
    updateBlastOffset,
    updateBlastDpl,
    updateBlastImage,
    loading,
    derived,
  };
};
